/**
 * Expand a THREE.Box3 around its center by a factor.
 *
 * Rationale (see AVA-337 Verification plan):
 * The design model is exact geometry; the as-built scan captures reality,
 * which may deviate from design. Cropping tightly to the design bbox would
 * clip real-world misalignment out of the scan — defeating verification.
 * Expanding the bbox (typically +20%) preserves surrounding scan context
 * so deviations stay visible when comparing design vs. as-built.
 *
 * Pure function. Returns a NEW THREE.Box3; does not mutate the input.
 *
 * @param {THREE.Box3} bbox    The source bounding box.
 * @param {number} factor      Expansion factor. 1.0 = no-op, 1.2 = +20% per side.
 *                             Values < 1.0 shrink (unusual but permitted).
 * @returns {THREE.Box3}       A new expanded Box3. Returns an empty Box3 if input is invalid.
 */
export function expandBbox(bbox, factor) {
  const THREE = window.THREE;
  if (!bbox || typeof bbox.getCenter !== 'function' || typeof bbox.getSize !== 'function') {
    return THREE ? new THREE.Box3() : bbox;
  }
  if (!Number.isFinite(factor) || factor <= 0) factor = 1;

  const center = bbox.getCenter(new THREE.Vector3());
  const size = bbox.getSize(new THREE.Vector3());
  const halfSize = size.multiplyScalar(factor * 0.5);

  const min = new THREE.Vector3(center.x - halfSize.x, center.y - halfSize.y, center.z - halfSize.z);
  const max = new THREE.Vector3(center.x + halfSize.x, center.y + halfSize.y, center.z + halfSize.z);

  return new THREE.Box3(min, max);
}

export default expandBbox;
